import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, ArrowRight, CalendarDays, CheckCircle2, ChevronLeft,
  ClipboardList, Download, FileText, ListChecks, Loader2, MapPin, Plus, RefreshCw,
  Save, Trash2, Users, X,
} from "lucide-react";
import { toast } from "sonner";
import { apiRequest, downloadFile } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useConfirm } from "@/components/useConfirm";

const STATUS_CLASSES = {
  bozza: "bg-amber-100 text-amber-800 border-amber-300",
  completato: "bg-blue-100 text-blue-800 border-blue-300",
  firmato: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

const SEVERITY_CONFIG = {
  critico: { label: "Critico", className: "bg-rose-100 text-rose-800 border-rose-300" },
  major: { label: "Maggiore", className: "bg-orange-100 text-orange-800 border-orange-300" },
  minor: { label: "Minore", className: "bg-amber-100 text-amber-800 border-amber-300" },
  info: { label: "Info", className: "bg-blue-100 text-blue-800 border-blue-300" },
};

const STEPS = [
  { key: "anagrafica", label: "Anagrafica", icon: ClipboardList },
  { key: "aree", label: "Aree ispezionate", icon: MapPin },
  { key: "rilievi", label: "Rilievi", icon: AlertTriangle },
  { key: "firme", label: "Firme & conclusione", icon: CheckCircle2 },
];

const EMPTY_PARTICIPANT = { name: "", role: "RSPP", signature_collected: false, notes: "" };
const EMPTY_FINDING = { title: "", description: "", severity: "minor", location_detail: "", proposed_action: "", target_due_date: "" };

export default function SiteInspections() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { confirm, ConfirmDialog } = useConfirm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiRequest(`/companies/${id}/site-inspections`);
      setList(r);
    } catch (err) { toast.error("Errore", { description: err.message }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const startNew = () => {
    setForm({
      inspection_date: new Date().toISOString().slice(0, 10),
      inspection_purpose: "periodica",
      weather_conditions: "",
      areas_inspected: [],
      participants: [{ ...EMPTY_PARTICIPANT }],
      findings: [],
      general_observations: "",
      next_inspection_due: "",
      status: "bozza",
    });
    setEditingId("__new__");
    setActiveStep(0);
  };

  const editExisting = (insp) => {
    setForm({
      inspection_date: insp.inspection_date || "",
      inspection_purpose: insp.inspection_purpose || "periodica",
      weather_conditions: insp.weather_conditions || "",
      areas_inspected: insp.areas_inspected || [],
      participants: insp.participants?.length ? insp.participants : [{ ...EMPTY_PARTICIPANT }],
      findings: insp.findings || [],
      general_observations: insp.general_observations || "",
      next_inspection_due: insp.next_inspection_due || "",
      status: insp.status || "bozza",
    });
    setEditingId(insp.id);
    setActiveStep(0);
  };

  const cancelEdit = () => { setEditingId(null); setForm(null); setActiveStep(0); };

  const save = async ({ regenerate = false } = {}) => {
    if (!form) return;
    setSaving(true);
    try {
      let saved;
      if (editingId === "__new__") {
        saved = await apiRequest(`/companies/${id}/site-inspections`, { method: "POST", body: JSON.stringify(form) });
        toast.success("Verbale creato");
      } else {
        saved = await apiRequest(`/companies/${id}/site-inspections/${editingId}`, { method: "PUT", body: JSON.stringify(form) });
        toast.success("Verbale aggiornato");
      }
      setEditingId(saved.id);
      await load();
      if (regenerate) {
        await downloadFile(`/companies/${id}/site-inspections/${saved.id}/generate-pdf`, `verbale_sopralluogo_${saved.inspection_date}.pdf`, { method: "POST" });
        toast.success("PDF generato e scaricato");
      }
    } catch (err) {
      toast.error("Salvataggio fallito", { description: err.message });
    } finally { setSaving(false); }
  };

  const generatePdf = async (insp) => {
    try {
      await downloadFile(`/companies/${id}/site-inspections/${insp.id}/generate-pdf`, `verbale_sopralluogo_${insp.inspection_date}.pdf`, { method: "POST" });
      toast.success("PDF verbale scaricato");
      await load();
    } catch (err) {
      toast.error("Generazione PDF fallita", { description: err.message });
    }
  };

  const promoteToPlan = async (insp) => {
    try {
      const r = await apiRequest(`/companies/${id}/site-inspections/${insp.id}/promote-to-improvement-plan`, { method: "POST" });
      toast.success("Azioni promosse al piano miglioramento", { description: `${r.promoted} create · ${r.skipped} già presenti` });
    } catch (err) { toast.error("Promozione fallita", { description: err.message }); }
  };

  const remove = async (insp) => {
    const ok = await confirm({
      title: "Eliminare il verbale?",
      description: `Il verbale del ${insp.inspection_date} sarà eliminato definitivamente.`,
      confirmLabel: "Elimina",
      destructive: true,
    });
    if (!ok) return;
    try {
      await apiRequest(`/companies/${id}/site-inspections/${insp.id}`, { method: "DELETE" });
      toast.success("Verbale eliminato");
      if (editingId === insp.id) cancelEdit();
      await load();
    } catch (err) { toast.error("Eliminazione fallita", { description: err.message }); }
  };

  // ---------- Step renderers ----------
  const StepAnagrafica = () => (
    <div className="space-y-4" data-testid="step-anagrafica">
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Data sopralluogo *</Label>
          <Input type="date" value={form.inspection_date} onChange={(e) => setForm({ ...form, inspection_date: e.target.value })} data-testid="inspection-date-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Tipologia *</Label>
          <select className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={form.inspection_purpose} onChange={(e) => setForm({ ...form, inspection_purpose: e.target.value })} data-testid="inspection-purpose-input">
            <option value="periodica">Periodica</option>
            <option value="post-incidente">Post-incidente</option>
            <option value="straordinaria">Straordinaria</option>
            <option value="iniziale">Iniziale / Accettazione</option>
            <option value="su segnalazione">Su segnalazione</option>
          </select>
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label>Condizioni meteo / ambientali</Label>
          <Input value={form.weather_conditions} onChange={(e) => setForm({ ...form, weather_conditions: e.target.value })} placeholder="Es. Soleggiato 22°C, lavorazioni regolari" data-testid="weather-input" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Partecipanti *</Label>
          <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, participants: [...form.participants, { ...EMPTY_PARTICIPANT }] })} data-testid="add-participant-button">
            <Plus className="h-3.5 w-3.5" /> Aggiungi
          </Button>
        </div>
        {form.participants.map((p, idx) => (
          <div key={idx} className="grid items-end gap-2 rounded-md border border-slate-200 p-3 md:grid-cols-[1.5fr_1fr_120px_40px]" data-testid={`participant-row-${idx}`}>
            <Input value={p.name} onChange={(e) => updateParticipant(idx, "name", e.target.value)} placeholder="Nome cognome" data-testid={`participant-name-${idx}`} />
            <select className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" value={p.role} onChange={(e) => updateParticipant(idx, "role", e.target.value)} data-testid={`participant-role-${idx}`}>
              {["RSPP", "ASPP", "Datore di Lavoro", "RLS", "Medico Competente", "Preposto", "Lavoratore", "Consulente esterno", "Altro"].map((r) => <option key={r}>{r}</option>)}
            </select>
            <label className="inline-flex items-center gap-2 text-xs">
              <input type="checkbox" checked={!!p.signature_collected} onChange={(e) => updateParticipant(idx, "signature_collected", e.target.checked)} data-testid={`participant-signed-${idx}`} />
              Firma raccolta
            </label>
            <Button type="button" variant="ghost" size="sm" className="text-rose-600" onClick={() => setForm({ ...form, participants: form.participants.filter((_, i) => i !== idx) })} data-testid={`remove-participant-${idx}`}><X className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );

  const updateParticipant = (idx, k, v) => {
    const next = [...form.participants];
    next[idx] = { ...next[idx], [k]: v };
    setForm({ ...form, participants: next });
  };

  const StepAree = () => {
    const [draft, setDraft] = useState("");
    const add = () => {
      const v = draft.trim();
      if (!v) return;
      setForm({ ...form, areas_inspected: [...form.areas_inspected, v] });
      setDraft("");
    };
    return (
      <div className="space-y-4" data-testid="step-aree">
        <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-900">
          <strong>Suggerimento:</strong> elenca tutte le aree/ambienti ispezionati (uffici, magazzini, laboratori, reparti, esterni, archivi...). Più granulare, più tracciabile.
        </div>
        <div className="flex gap-2">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="Es. Magazzino A, scaffale 3 · Ufficio open space piano 2" data-testid="area-input" />
          <Button type="button" onClick={add} disabled={!draft.trim()} className="bg-blue-900 hover:bg-blue-800" data-testid="add-area-button"><Plus className="h-3.5 w-3.5" /> Aggiungi area</Button>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="areas-list">
          {form.areas_inspected.length === 0 && <p className="text-sm text-slate-500">Nessuna area aggiunta. Aggiungine almeno una.</p>}
          {form.areas_inspected.map((a, idx) => (
            <Badge key={idx} className="flex items-center gap-1 bg-slate-100 px-3 py-1.5 text-sm text-slate-800" data-testid={`area-chip-${idx}`}>
              <MapPin className="h-3 w-3" /> {a}
              <button type="button" onClick={() => setForm({ ...form, areas_inspected: form.areas_inspected.filter((_, i) => i !== idx) })} className="ml-1 text-slate-500 hover:text-rose-600" data-testid={`remove-area-${idx}`}><X className="h-3 w-3" /></button>
            </Badge>
          ))}
        </div>
      </div>
    );
  };

  const updateFinding = (idx, k, v) => {
    const next = [...form.findings];
    next[idx] = { ...next[idx], [k]: v };
    setForm({ ...form, findings: next });
  };

  const StepRilievi = () => (
    <div className="space-y-4" data-testid="step-rilievi">
      <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
        <strong>Suggerimento:</strong> aggiungi un rilievo per ogni non-conformità o anomalia. Lascia vuoto se tutto OK. Le azioni proposte verranno poi promosse al <strong>Piano di Miglioramento</strong>.
      </div>
      <div className="flex justify-end">
        <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, findings: [...form.findings, { ...EMPTY_FINDING }] })} data-testid="add-finding-button">
          <Plus className="h-3.5 w-3.5" /> Aggiungi rilievo
        </Button>
      </div>
      {form.findings.length === 0 ? (
        <div className="rounded-md border border-dashed p-6 text-center text-sm text-emerald-700" data-testid="no-findings">
          <CheckCircle2 className="mx-auto h-8 w-8" />
          Nessuna non-conformità — sopralluogo conforme. Puoi procedere al passo successivo.
        </div>
      ) : (
        form.findings.map((f, idx) => (
          <Card key={idx} className="shadow-none" data-testid={`finding-card-${idx}`}>
            <CardContent className="space-y-3 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Rilievo #{idx + 1}</p>
                <Button type="button" variant="ghost" size="sm" className="text-rose-600" onClick={() => setForm({ ...form, findings: form.findings.filter((_, i) => i !== idx) })} data-testid={`remove-finding-${idx}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Titolo *</Label>
                  <Input value={f.title} onChange={(e) => updateFinding(idx, "title", e.target.value)} placeholder="Es. Estintore scaduto nel magazzino A" data-testid={`finding-title-${idx}`} />
                </div>
                <div className="space-y-1.5">
                  <Label>Severity *</Label>
                  <select className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={f.severity} onChange={(e) => updateFinding(idx, "severity", e.target.value)} data-testid={`finding-severity-${idx}`}>
                    <option value="info">Info</option>
                    <option value="minor">Minore</option>
                    <option value="major">Maggiore</option>
                    <option value="critico">Critico</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Luogo specifico</Label>
                  <Input value={f.location_detail || ""} onChange={(e) => updateFinding(idx, "location_detail", e.target.value)} placeholder="Es. Magazzino A, scaffale 3" data-testid={`finding-location-${idx}`} />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Descrizione</Label>
                  <Textarea rows={2} value={f.description || ""} onChange={(e) => updateFinding(idx, "description", e.target.value)} data-testid={`finding-description-${idx}`} />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Azione correttiva proposta</Label>
                  <Textarea rows={2} value={f.proposed_action || ""} onChange={(e) => updateFinding(idx, "proposed_action", e.target.value)} placeholder="Es. Sostituzione estintore con uno conforme entro 7gg" data-testid={`finding-action-${idx}`} />
                </div>
                <div className="space-y-1.5">
                  <Label>Scadenza azione</Label>
                  <Input type="date" value={f.target_due_date || ""} onChange={(e) => updateFinding(idx, "target_due_date", e.target.value)} data-testid={`finding-due-${idx}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );

  const StepFirme = () => (
    <div className="space-y-4" data-testid="step-firme">
      <div className="space-y-1.5">
        <Label>Osservazioni generali</Label>
        <Textarea rows={4} value={form.general_observations} onChange={(e) => setForm({ ...form, general_observations: e.target.value })} placeholder="Sintesi dell'esito complessivo del sopralluogo..." data-testid="observations-input" />
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Prossimo sopralluogo previsto</Label>
          <Input type="date" value={form.next_inspection_due || ""} onChange={(e) => setForm({ ...form, next_inspection_due: e.target.value })} data-testid="next-inspection-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Stato del verbale</Label>
          <select className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="status-input">
            <option value="bozza">Bozza</option>
            <option value="completato">Completato</option>
            <option value="firmato">Firmato</option>
          </select>
        </div>
      </div>
      <div className="rounded-md border border-blue-200 bg-blue-50/40 p-3" data-testid="firme-recap">
        <h4 className="font-semibold text-slate-950">Riepilogo firme</h4>
        <ul className="mt-2 space-y-1 text-sm">
          {form.participants.map((p, idx) => (
            <li key={idx} className="flex items-center justify-between">
              <span>{p.name || <em className="text-slate-400">Partecipante senza nome</em>} <span className="text-slate-500">— {p.role}</span></span>
              <Badge className={p.signature_collected ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}>{p.signature_collected ? "Firma raccolta" : "In attesa"}</Badge>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  // ---------- Render ----------
  return (
    <div className="space-y-6" data-testid="site-inspections-page">
      <ConfirmDialog />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={() => navigate(`/aziende/${id}`)} data-testid="site-inspections-back-button"><ArrowLeft className="h-4 w-4" /> Scheda azienda</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="site-inspections-refresh-button"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></Button>
          {!editingId && (
            <Button onClick={startNew} className="bg-blue-900 hover:bg-blue-800" data-testid="new-inspection-button"><Plus className="h-4 w-4" /> Nuovo verbale</Button>
          )}
        </div>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Verbali di Sopralluogo</p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950" data-testid="site-inspections-title">Compilazione guidata verbali</h2>
        <p className="mt-2 text-slate-600">Crea verbali di sopralluogo strutturati in 4 passaggi: anagrafica → aree ispezionate → rilievi e non-conformità → firme & conclusioni. Genera il PDF stampabile e promuovi le azioni correttive al piano di miglioramento con un click.</p>
      </section>

      {editingId && form && (
        <Card className="shadow-none" data-testid="inspection-wizard">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="font-heading text-xl">{editingId === "__new__" ? "Nuovo verbale" : "Modifica verbale"}</CardTitle>
              <Button variant="outline" size="sm" onClick={cancelEdit} data-testid="cancel-wizard-button"><X className="h-3.5 w-3.5" /> Chiudi</Button>
            </div>
            <div className="mt-3 flex items-center gap-2 overflow-x-auto" data-testid="wizard-stepper">
              {STEPS.map((s, idx) => {
                const Icon = s.icon;
                const active = idx === activeStep;
                return (
                  <button key={s.key} type="button" onClick={() => setActiveStep(idx)} className={`flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${active ? "border-blue-900 bg-blue-900 text-white" : "border-slate-200 bg-white text-slate-700"}`} data-testid={`step-tab-${s.key}`}>
                    <Icon className="h-3.5 w-3.5" /> {idx + 1}. {s.label}
                  </button>
                );
              })}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeStep === 0 && <StepAnagrafica />}
            {activeStep === 1 && <StepAree />}
            {activeStep === 2 && <StepRilievi />}
            {activeStep === 3 && <StepFirme />}
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4">
              <Button type="button" variant="outline" disabled={activeStep === 0} onClick={() => setActiveStep(activeStep - 1)} data-testid="wizard-prev-button"><ChevronLeft className="h-4 w-4" /> Indietro</Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => save({})} disabled={saving} data-testid="wizard-save-button">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Salva bozza
                </Button>
                {activeStep < STEPS.length - 1 ? (
                  <Button type="button" onClick={() => setActiveStep(activeStep + 1)} className="bg-blue-900 hover:bg-blue-800" data-testid="wizard-next-button">Avanti <ArrowRight className="h-4 w-4" /></Button>
                ) : (
                  <Button type="button" onClick={() => save({ regenerate: true })} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="wizard-finalize-button">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                    Salva e genera PDF
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="shadow-none" data-testid="inspections-list-card">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2"><ListChecks className="h-5 w-5" /> Verbali esistenti ({list.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading && <p className="text-center text-sm text-slate-500" data-testid="inspections-loading"><Loader2 className="inline h-4 w-4 animate-spin" /> Caricamento...</p>}
          {!loading && list.length === 0 && (
            <Alert data-testid="inspections-empty">
              <AlertTitle>Nessun verbale ancora creato</AlertTitle>
              <AlertDescription>Clicca "Nuovo verbale" per iniziare la compilazione guidata del primo verbale di sopralluogo.</AlertDescription>
            </Alert>
          )}
          {list.map((insp) => {
            const findCount = (insp.findings || []).length;
            const critCount = (insp.findings || []).filter((f) => f.severity === "critico").length;
            return (
              <div key={insp.id} className="rounded-md border border-slate-200 p-4" data-testid={`inspection-row-${insp.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-heading text-lg font-semibold text-slate-950" data-testid={`inspection-date-${insp.id}`}>
                        <CalendarDays className="inline h-4 w-4 text-blue-900" /> {insp.inspection_date}
                      </p>
                      <Badge className={`border ${STATUS_CLASSES[insp.status] || ""}`} data-testid={`inspection-status-${insp.id}`}>{insp.status}</Badge>
                      <Badge className="bg-slate-100 text-slate-700">{insp.inspection_purpose}</Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
                      <span data-testid={`inspection-participants-${insp.id}`}><Users className="inline h-3 w-3" /> {(insp.participants || []).length} partecipanti</span>
                      <span><MapPin className="inline h-3 w-3" /> {(insp.areas_inspected || []).length} aree</span>
                      <span data-testid={`inspection-findings-${insp.id}`}><AlertTriangle className="inline h-3 w-3" /> {findCount} rilievi {critCount > 0 && <Badge className="ml-1 bg-rose-100 text-rose-700 text-[10px]">{critCount} critici</Badge>}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <Progress value={insp.completion_percentage} className="h-1.5 max-w-[200px]" />
                      <span className="text-xs text-slate-500">{insp.completion_percentage}%</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => editExisting(insp)} data-testid={`edit-inspection-${insp.id}`}>Modifica</Button>
                    <Button variant="outline" size="sm" onClick={() => generatePdf(insp)} data-testid={`pdf-inspection-${insp.id}`}><Download className="h-3.5 w-3.5" /> PDF</Button>
                    {findCount > 0 && <Button variant="outline" size="sm" onClick={() => promoteToPlan(insp)} data-testid={`promote-inspection-${insp.id}`}><ListChecks className="h-3.5 w-3.5" /> → Piano</Button>}
                    <Button variant="outline" size="sm" className="text-rose-700" onClick={() => remove(insp)} data-testid={`delete-inspection-${insp.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
